import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import Navbar from '../components/Navbar';
import { blogService } from '../services/blogService';

/* ─── Responsive hook ─────────────────────────────────────────── */
const useWindowSize = () => {
    const [size, setSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 1200,
        height: typeof window !== 'undefined' ? window.innerHeight : 800,
    });

    useEffect(() => {
        const handle = () =>
            setSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);

    return size;
};

/* ─── Scoped CSS injected once ────────────────────────────────── */
const STYLES = `
    :root {
        --accent-color: #C9A96E;
        --accent-dark:  #B8935D;
        --text-light:   #F5F1ED;
        --bg-main:      #0F0F0F;
        --surface:      rgba(201,169,110,0.05);
        --border:       rgba(201,169,110,0.15);
        --border-hover: rgba(201,169,110,1);
    }

    /* ── Layout ── */
    .bd-wrap       { max-width:1400px; margin:0 auto; padding:60px 5% }
    .bd-grid       { gap:50px; align-items:start }

    @media(min-width:993px){
        .bd-grid   { grid-template-columns:1fr 340px }
    }
    @media(max-width:992px){
        .bd-grid   { grid-template-columns:1fr }
        .bd-sidebar{ position:static !important }
        .bd-wrap   { padding:40px 5% }
    }
    @media(max-width:600px){
        .bd-wrap   { padding:24px 4% }
    }

    /* ── Hero image ── */
    .bd-img-wrap   {
        overflow:hidden; border-radius:20px;
        margin-bottom:50px;
        box-shadow:0 10px 40px rgba(0,0,0,.5);
    }
    .bd-img        {
        width:100%; display:block; object-fit:cover;
        height:clamp(200px,40vw,500px);
        transition:transform .6s ease;
    }
    @media(hover:hover){
        .bd-img-wrap:hover .bd-img { transform:scale(1.07) }
    }

    /* ── Title ── */
    .bd-title      {
        font-size:clamp(26px,5vw,52px);
        font-weight:300; line-height:1.3;
        color:var(--text-light); margin-bottom:25px;
    }

    /* ── Meta row ── */
    .bd-meta       {
        display:flex; gap:16px; flex-wrap:wrap;
        color:rgba(245,241,237,.6); font-size:15px; align-items:center;
    }

    /* ── Rich text content ── */
    .bd-content    {
        font-size:clamp(16px,2vw,18px);
        line-height:1.9; color:rgba(245,241,237,.9);
    }
    .bd-content h2 { font-size:clamp(20px,3vw,28px); color:var(--text-light); margin-top:2em }
    .bd-content h3 { font-size:clamp(17px,2.5vw,22px); color:var(--accent-color); margin-top:1.5em }
    .bd-content p  { margin-bottom:1.4em }
    .bd-content a  { color:var(--accent-color); text-decoration:underline }
    .bd-content img{ max-width:100%; width:100%; height:auto; border-radius:12px; margin:1em 0; display:block; }
    .bd-content ul,
    .bd-content ol { padding-left:1.6em; margin-bottom:1.4em }
    .bd-content blockquote {
        border-left:3px solid var(--accent-color);
        padding:16px 24px; margin:1.5em 0;
        background:rgba(201,169,110,.06);
        border-radius:0 12px 12px 0;
        font-style:italic; color:rgba(245,241,237,.8);
    }
	.bd-content table{
    width:100%;
    display:block;
    overflow-x:auto;
    white-space:nowrap;
    border-collapse:collapse;
	}

	.bd-content iframe,
	.bd-content video{
    max-width:100%;
	}
    /* ── Sidebar widgets ── */
    .bd-widget     {
        margin-bottom:24px; padding:28px 24px;
        background:var(--surface);
        border-radius:16px; border:1px solid var(--border);
    }
	@media(max-width:600px){
    .bd-widget{
        padding:20px 16px;
    }
	}
    .bd-widget-title {
        font-size:18px; font-weight:500;
        color:var(--text-light); margin-bottom:18px;
        display:flex; align-items:center; gap:10px;
    }

    /* ── Tags ── */
    .bd-tag {
        display:inline-block; padding:7px 15px;
        background:rgba(201,169,110,.1);
        border:1px solid rgba(201,169,110,.3);
        border-radius:20px; font-size:13px;
        color:var(--accent-color); cursor:pointer;
        transition:all .25s ease; margin:4px;
    }
    @media(hover:hover){
        .bd-tag:hover {
            background:var(--accent-color);
            color:#000; transform:translateY(-2px);
        }
    }

    /* ── Related post card ── */
    .bd-related-link {
        display:flex; gap:14px; text-decoration:none;
        transition:transform .3s ease;
    }
	@media(max-width:600px){

    .bd-related-link{
        gap:10px;
    }

    .bd-related-thumb{
        width:65px;
        height:65px;
    }

    .bd-related-title{
        font-size:13px;
    }
	}
    @media(hover:hover){
        .bd-related-link:hover { transform:translateX(5px) }
    }
    .bd-related-thumb {
        width:76px; height:76px; flex-shrink:0;
        border-radius:10px; overflow:hidden;
    }
    .bd-related-thumb img {
        width:100%; height:100%; object-fit:cover;
        transition:transform .4s ease;
    }
    @media(hover:hover){
        .bd-related-link:hover .bd-related-thumb img { transform:scale(1.12) }
    }
    .bd-related-title {
        font-size:14px; font-weight:500;
        color:var(--text-light); line-height:1.45;
        display:-webkit-box; -webkit-line-clamp:2;
        -webkit-box-orient:vertical; overflow:hidden;
        margin-bottom:6px;
    }
    .bd-related-date { font-size:12px; color:rgba(245,241,237,.5) }

    /* ── Contact form in sidebar ── */
    .bd-input {
        width:100%; box-sizing:border-box;
        padding:12px 16px;
        background:rgba(15,15,15,.6);
        border:1px solid rgba(201,169,110,.3);
        border-radius:8px; color:var(--text-light);
        font-size:14px; font-family:inherit;
        outline:none; transition:border-color .3s ease;
    }
	@media(max-width:600px){

    .bd-input{
        font-size:16px;
    }

    .bd-submit{
        min-height:48px;
    }
	}
    .bd-input:focus { border-color:var(--accent-color) }
    .bd-textarea    { resize:vertical }
    .bd-submit {
        width:100%; padding:13px;
        background:linear-gradient(135deg,var(--accent-color) 0%,var(--accent-dark) 100%);
        color:#000; border:none; border-radius:8px;
        font-size:14px; font-weight:700; cursor:pointer;
        transition:all .3s ease;
        box-shadow:0 4px 15px rgba(201,169,110,.3);
    }
    @media(hover:hover){
        .bd-submit:hover {
            transform:translateY(-2px);
            box-shadow:0 6px 20px rgba(201,169,110,.45);
        }
    }
    .bd-submit:active { transform:translateY(0) }

    /* ── Author section ── */
    .bd-author-card {
        display:flex; flex-direction:column;
        align-items:center; gap:28px;
        padding:44px 32px;
        background:rgba(15,15,15,.5);
        border-radius:24px; border:1px solid rgba(201,169,110,.2);
        text-align:center;
        transition:all .4s ease;
        backdrop-filter:blur(10px);
    }
    @media(hover:hover){
        .bd-author-card:hover {
            transform:translateY(-5px);
            box-shadow:0 20px 50px rgba(201,169,110,.2);
            border-color:var(--accent-color);
        }
    }

    /* ── Stats row ── */
    .bd-stats {
        display:flex; gap:clamp(20px,5vw,40px);
        justify-content:center; flex-wrap:wrap; margin-bottom:28px;
    }

    /* ── CTA buttons ── */
    .bd-btn-primary, .bd-btn-outline {
        padding:14px 28px; border-radius:50px;
        font-size:15px; font-weight:700; cursor:pointer;
        transition:all .4s cubic-bezier(.4,0,.2,1);
        display:inline-flex; align-items:center; gap:8px;
        text-decoration:none; letter-spacing:.5px;
    }
    .bd-btn-primary {
        background:linear-gradient(135deg,var(--accent-color) 0%,var(--accent-dark) 100%);
        color:#000; border:none;
        box-shadow:0 4px 15px rgba(201,169,110,.3);
    }
    @media(hover:hover){
        .bd-btn-primary:hover {
            transform:translateY(-2px);
            box-shadow:0 6px 20px rgba(201,169,110,.5);
        }
    }
    .bd-btn-outline {
        background:transparent; color:var(--accent-color);
        border:2px solid var(--accent-color);
    }
    @media(hover:hover){
        .bd-btn-outline:hover {
            background:rgba(201,169,110,.15);
            transform:translateY(-2px);
            box-shadow:0 6px 20px rgba(201,169,110,.3);
        }
    }

    /* ── CTA section buttons (larger) ── */
    .bd-cta-primary, .bd-cta-outline {
        padding:16px 36px; border-radius:50px;
        font-size:16px; font-weight:700; cursor:pointer;
        transition:all .4s cubic-bezier(.4,0,.2,1);
        display:inline-flex; align-items:center; gap:10px;
        text-decoration:none; letter-spacing:.5px;
    }
    .bd-cta-primary {
        background:linear-gradient(135deg,var(--accent-color) 0%,var(--accent-dark) 100%);
        color:#000; border:none;
        box-shadow:0 4px 15px rgba(201,169,110,.3);
    }
    @media(hover:hover){
        .bd-cta-primary:hover {
            transform:translateY(-3px);
            box-shadow:0 8px 25px rgba(201,169,110,.5);
        }
    }
    .bd-cta-outline {
        background:transparent; color:var(--accent-color);
        border:2px solid var(--accent-color);
    }
    @media(hover:hover){
        .bd-cta-outline:hover {
            background:rgba(201,169,110,.15);
            transform:translateY(-3px);
            box-shadow:0 8px 25px rgba(201,169,110,.3);
        }
    }

    /* ── Loading / not-found ── */
    .bd-state {
        padding:120px 5%; text-align:center;
        color:var(--text-light); font-size:20px;
        min-height:100vh; display:flex;
        align-items:center; justify-content:center;
        flex-direction:column; gap:20px;
        background:var(--bg-main);
    }

    /* ── Back link ── */
    .bd-back {
        color:var(--accent-color); text-decoration:none;
        display:inline-flex; align-items:center; gap:8px;
        font-size:15px; font-weight:500;
        transition:gap .3s ease;
    }
    @media(hover:hover){
        .bd-back:hover { gap:14px }
    }

    /* ── Category pill ── */
    .bd-category {
        display:inline-block; padding:6px 20px;
        background:var(--accent-color);
        color:#000; font-size:13px; font-weight:600;
        border-radius:20px; text-transform:capitalize;
        margin-bottom:20px;
    }

    /* ── Divider ── */
    .bd-divider {
        border:none; height:1px;
        background:rgba(201,169,110,.15);
        margin:0 0 40px;
    }

    /* ── Section headings ── */
    .bd-section-head {
        text-align:center; margin-bottom:48px;
    }
    .bd-section-head h2 {
        font-size:clamp(24px,4vw,38px);
        font-weight:300; color:var(--text-light);
        margin-bottom:14px; letter-spacing:-.5px;
    }
    .bd-gold-line {
        width:80px; height:3px; margin:0 auto;
        background:linear-gradient(90deg,transparent,var(--accent-color),transparent);
    }

    /* ── Button row ── */
    .bd-btn-row{
    display:flex;
    gap:14px;
    justify-content:center;
    flex-wrap:wrap;
}

@media(max-width:600px){

    .bd-btn-row{
        flex-direction:column;
        width:100%;
    }

    .bd-btn-row a{
        width:100%;
        justify-content:center;
        text-align:center;
    }
}

    /* ── Footer ── */
    footer {
        background:var(--bg-main);
        text-align:center; padding:40px 5%;
        border-top:1px solid rgba(201,169,110,.1);
        color:rgba(245,241,237,.5); font-size:13px;
    }
    footer img{
    display:block;
    margin:0 auto 16px;
    width:clamp(120px,30vw,160px);
    height:auto;
	}
@media(max-width:480px){

    .bd-wrap{
        padding:20px 4%;
    }

    .bd-title{
        font-size:28px;
        line-height:1.35;
    }

    .bd-meta{
        font-size:13px;
        gap:10px;
    }

	.bd-img {
    width: 100%;
    height: clamp(220px, 45vw, 550px);
    object-fit: cover;
	}
    .bd-content{
        font-size:15px;
        line-height:1.8;
    }

    .bd-widget-title{
        font-size:16px;
    }

    .bd-category{
        font-size:12px;
        padding:5px 14px;
    }
}	
`;

/* ─── Inject styles once ───────────────────────────────────────── */
const StyleTag = () => {
    useEffect(() => {
        const id = 'bd-styles';
        if (!document.getElementById(id)) {
            const el = document.createElement('style');
            el.id = id;
            el.textContent = STYLES;
            document.head.appendChild(el);
        }
        return () => {
            const el = document.getElementById('bd-styles');
            if (el) el.remove();
        };
    }, []);
    return null;
};

/* ─── Contact form (no <form> tag to avoid reload) ────────────── 
const ContactWidget = () => {
    const [fields, setFields] = useState({ name: '', email: '', message: '' });
    const [sent, setSent]     = useState(false);
    const [busy, setBusy]     = useState(false);

    const change = useCallback(e =>
        setFields(p => ({ ...p, [e.target.name]: e.target.value })), []);

    const submit = useCallback(async () => {
        if (!fields.name || !fields.email || !fields.message) return;
        setBusy(true);
        // Replace with your actual API call:
        await new Promise(r => setTimeout(r, 900));
        setSent(true);
        setBusy(false);
    }, [fields]);

    return (
        <div className="bd-widget" style={{
            background:'linear-gradient(135deg,rgba(201,169,110,.1) 0%,rgba(201,169,110,.05) 100%)',
            border:'1px solid rgba(201,169,110,.2)'
        }}>
            <h3 className="bd-widget-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="var(--accent-color)" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Get in Touch
            </h3>
            <p style={{ fontSize:'14px', color:'rgba(245,241,237,.7)',
                        marginBottom:'22px', lineHeight:'1.6' }}>
                Questions about our design services? Let's talk!
            </p>

            {sent ? (
                <p style={{ textAlign:'center', color:'var(--accent-color)',
                            fontWeight:'600', padding:'12px 0' }}>
                    ✓ Message sent! We'll reply soon.
                </p>
            ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                    <input className="bd-input" name="name"
                           placeholder="Your Name" value={fields.name}
                           onChange={change} autoComplete="name" />
                    <input className="bd-input" name="email" type="email"
                           placeholder="Your Email" value={fields.email}
                           onChange={change} autoComplete="email" />
                    <textarea className="bd-input bd-textarea" name="message"
                              placeholder="Your Message" rows="4"
                              value={fields.message} onChange={change} />
                    <button className="bd-submit" onClick={submit} disabled={busy}>
                        {busy ? 'Sending…' : 'Send Message'}
                    </button>
                </div>
            )}
        </div>
    );
}; */

/* ─── SVG icons ───────────────────────────────────────────────── */
const CalIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const EyeIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);
const ArrowIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5">
        <line x1="5" y1="12" x2="19" y2="12"/>
        <polyline points="12 5 19 12 12 19"/>
    </svg>
);
const ChatIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
);
const StarIcon = ({ size = 24, fill = false }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
         fill={fill ? 'var(--accent-color)' : 'none'}
         stroke="var(--accent-color)" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02
                         12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
);

/* ─── Main component ───────────────────────────────────────────── */
const BlogDetailPage = () => {
    const { slug } = useParams();
    const { width } = useWindowSize();
    const isMobile  = width <= 600;
    const isTablet  = width <= 992;

    const [blog, setBlog]               = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading]         = useState(true);

    /* ── Data fetching ── */
    useEffect(() => {
        setLoading(true);
        setBlog(null);

        blogService.getBlogBySlug(slug)
            .then(data => {
                setBlog(data);
                setLoading(false);

                blogService.getAllBlogs()
                    .then(all => {
                        setRelatedBlogs(
                            all.filter(b => b.category === data.category && b._id !== data._id)
                               .slice(0, 3)
                        );
                    })
                    .catch(console.error);
            })
            .catch(err => { console.error(err); setLoading(false); });
    }, [slug]);

    /* ── GSAP entrance animations old ──
    useEffect(() => {
        if (!blog) return;

        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        tl.fromTo('.blog-detail-back',    { x: -30, opacity: 0 }, { x: 0, opacity: 1, duration: .6 })
          .fromTo('.blog-detail-title',   { y: 40,  opacity: 0 }, { y: 0, opacity: 1, duration: .8 }, '-=.3')
          .fromTo('.blog-detail-meta',    { y: 20,  opacity: 0 }, { y: 0, opacity: 1, duration: .6 }, '-=.4')
          .fromTo('.blog-detail-image',   { y: 60,  opacity: 0, scale: .95 },
                                          { y: 0,   opacity: 1, scale: 1, duration: 1 }, '-=.3')
          .fromTo('.blog-detail-content', { y: 40,  opacity: 0 }, { y: 0, opacity: 1, duration: .8 }, '-=.4');
    }, [blog]); -- */
	
	/* ── GSAP entrance animations new 
	useEffect(() => {

    if (!blog) return;

    const ctx = gsap.context(() => {

        const tl = gsap.timeline({
            defaults:{ ease:'power3.out' }
        });

        tl.fromTo(
            '.blog-detail-back',
            { x:-30, opacity:0 },
            { x:0, opacity:1, duration:.6 }
        );

    });

    return () => ctx.revert();

}, [blog]);

    /* ── Loading / Error states ── */
    if (loading) return (
        <>
            <StyleTag />
            <Navbar />
            <div className="bd-state">
                <div style={{
                    width: 48, height: 48, border: '3px solid rgba(201,169,110,.2)',
                    borderTop: '3px solid var(--accent-color)',
                    borderRadius: '50%', animation: 'spin .8s linear infinite'
                }} />
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                <p style={{ color: 'rgba(245,241,237,.6)' }}>Loading article…</p>
            </div>
        </>
    );

    if (!blog) return (
        <>
            <StyleTag />
            <Navbar />
            <div className="bd-state">
                <p>Article not found.</p>
                <Link to="/blog" className="bd-btn-primary">← Back to Blog</Link>
            </div>
        </>
    );

    const fmtDate = (d, opts) => new Date(d).toLocaleDateString('en-US', opts);

    return (
        <div style={{ background: 'var(--bg-main)', minHeight: '100vh' }}>
            <StyleTag />

            {/* ── SEO ── */}
            <Helmet>
                <title>{blog.metaTitle}</title>
                <meta name="description"  content={blog.metaDescription} />
                <meta name="keywords"     content={blog.metaKeywords || blog.tags?.join(', ')} />
                <meta name="viewport"     content="width=device-width, initial-scale=1.0" />
                <meta property="og:title"       content={blog.metaTitle} />
                <meta property="og:description" content={blog.metaDescription} />
                <meta property="og:image"       content={blog.featuredImage} />
                <meta property="og:type"        content="article" />
                <meta name="twitter:card"        content="summary_large_image" />
                <meta name="twitter:title"       content={blog.metaTitle} />
                <meta name="twitter:description" content={blog.metaDescription} />
                <meta name="twitter:image"       content={blog.featuredImage} />
            </Helmet>

            <Navbar />

            {/* ════════════════════ HERO SECTION ════════════════════ */}
            <section style={{ paddingTop: isMobile ? 70 : 100, background: 'var(--bg-main)' }}>
                <div className="bd-wrap">

                    {/* Back link */}
                    <div className="blog-detail-back" style={{ marginBottom: 28 }}>
                        <Link to="/blog" className="bd-back">← Back to Blog</Link>
                    </div>

                    {/* Two-column grid (stacks on ≤992px) */}
                    <div className="bd-grid">

                        {/* ── Left column ── */}
                        <div>
                            {/* Header block */}
                            <div style={{ paddingBottom: 36, marginBottom: 36 }}>
                                <hr className="bd-divider" style={{ margin: '0 0 36px' }} />

                                <span className="bd-category">{blog.category}</span>

                                <h1 className="blog-detail-title bd-title">{blog.title}</h1>

                                <div className="bd-meta blog-detail-meta">
                                    <span style={{ display:'flex', alignItems:'center', gap:7 }}>
                                        <CalIcon />
                                        {fmtDate(blog.createdAt, { year:'numeric', month:'long', day:'numeric' })}
                                    </span>
                                    <span>•</span>
                                    <span style={{ display:'flex', alignItems:'center', gap:7 }}>
                                        <EyeIcon />
                                        {(blog.views || 0).toLocaleString()} views
                                    </span>
                                </div>
                            </div>

                            {/* Featured image */}
                            <div className="bd-img-wrap blog-detail-image">
                                <img
                                    className="bd-img"
                                    src={blog.featuredImage}
                                    alt={blog.title}
                                    loading="lazy"
                                />
                            </div>

                            {/* Rich-text content */}
                            <div
                                className="blog-detail-content bd-content"
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                            />
                        </div>

                        {/* ── Sidebar ── */}
                        <aside className="bd-sidebar" style={{ position: isTablet ? 'static' : 'sticky', top: 110 }}>

                            {/* Tags */}
                            {blog.tags?.length > 0 && (
                                <div className="bd-widget">
                                    <h3 className="bd-widget-title">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                             stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                                            <line x1="7" y1="7" x2="7.01" y2="7"/>
                                        </svg>
                                        Tags
                                    </h3>
                                    <div>
                                        {blog.tags.map((tag, i) => (
                                            <span key={i} className="bd-tag">#{tag}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Related posts */}
                            {relatedBlogs.length > 0 && (
                                <div className="bd-widget">
                                    <h3 className="bd-widget-title">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                             stroke="var(--accent-color)" strokeWidth="2">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                        </svg>
                                        Related Posts
                                    </h3>
                                    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
                                        {relatedBlogs.map(rb => (
                                            <Link key={rb._id} to={`/blog/${rb.slug}`}
                                                  className="bd-related-link">
                                                <div className="bd-related-thumb">
                                                    <img src={rb.featuredImage} alt={rb.title} loading="lazy" />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <p className="bd-related-title">{rb.title}</p>
                                                    <p className="bd-related-date">
                                                        {fmtDate(rb.createdAt, { month:'short', day:'numeric', year:'numeric' })}
                                                    </p>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Contact form (no <form> tag) */}
                           
                        </aside>
                    </div>
                </div>
            </section>

            {/* ════════════════════ AUTHOR SECTION ════════════════════ */}
            <section style={{
                background:'linear-gradient(135deg,rgba(201,169,110,.08) 0%,rgba(201,169,110,.03) 100%)',
                padding:`${isMobile ? 60 : 100}px 5%`,
                borderTop:'1px solid rgba(201,169,110,.1)',
                position:'relative', overflow:'hidden'
            }}>
                {/* Decorative glow */}
                <div style={{
                    position:'absolute', top:'50%', left:'50%',
                    transform:'translate(-50%,-50%)',
                    width:'500px', height:'500px',
                    background:'radial-gradient(circle,rgba(201,169,110,.1) 0%,transparent 70%)',
                    borderRadius:'50%', pointerEvents:'none'
                }} />

                <div style={{ maxWidth:1000, margin:'0 auto', position:'relative', zIndex:1 }}>
                    <div className="bd-section-head">
                        <h2>About the <span style={{ color:'var(--accent-color)' }}>Author</span></h2>
                        <div className="bd-gold-line" />
                    </div>

                    <div className="bd-author-card">
                        {/* Avatar */}
                        <div style={{
                            width:110, height:110, borderRadius:'50%',
                            background:'linear-gradient(135deg,var(--accent-color) 0%,var(--accent-dark) 100%)',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            boxShadow:'0 10px 30px rgba(201,169,110,.35)',
                            border:'4px solid rgba(201,169,110,.25)',
                            flexShrink: 0
                        }}>
                            <svg width="52" height="52" viewBox="0 0 24 24" fill="none"
                                 stroke="#000" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                        </div>

                        <div style={{ maxWidth:700 }}>
                            <h3 style={{ fontSize:isMobile?'24px':'32px', fontWeight:400,
                                         color:'var(--text-light)', marginBottom:14,
                                         letterSpacing:'-.5px' }}>
                                Hav'n Design Team
                            </h3>

                            <div style={{
                                display:'inline-flex', alignItems:'center', gap:12,
                                marginBottom:22, padding:'7px 18px',
                                background:'rgba(201,169,110,.1)', borderRadius:25,
                                border:'1px solid rgba(201,169,110,.2)'
                            }}>
                                <StarIcon size={16} />
                                <span style={{ fontSize:14, color:'var(--accent-color)', fontWeight:600 }}>
                                    25+ Years Experience
                                </span>
                            </div>

                            <p style={{ fontSize:isMobile?'15px':'17px',
                                        color:'rgba(245,241,237,.8)',
                                        lineHeight:1.8, marginBottom:26 }}>
                                Our expert design team brings 25+ years of combined experience
                                creating stunning interiors. We're passionate about transforming
                                spaces and sharing our knowledge with design enthusiasts worldwide.
                            </p>

                            {/* Stats */}
                            <div className="bd-stats">
                                {[['48+','Segments'],['8','Studios'],['25+','Years']].map(([n,l]) => (
                                    <div key={l}>
                                        <div style={{ fontSize:isMobile?'22px':'28px', fontWeight:600,
                                                      color:'var(--accent-color)', marginBottom:4 }}>{n}</div>
                                        <div style={{ fontSize:12, color:'rgba(245,241,237,.6)',
                                                      textTransform:'uppercase', letterSpacing:1 }}>{l}</div>
                                    </div>
                                ))}
                            </div>

                            {/* CTA buttons */}
                            <div className="bd-btn-row">
                                <Link to="/blog" className="bd-btn-primary">
                                    View All Posts <ArrowIcon />
                                </Link>
                                <Link to="/contact" className="bd-btn-outline">
                                    Get in Touch <ChatIcon />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ════════════════════ CTA SECTION ════════════════════ */}
            <section style={{
                background:'linear-gradient(135deg,rgba(201,169,110,.12) 0%,rgba(201,169,110,.06) 100%)',
                padding:`${isMobile ? 60 : 100}px 5%`,
                position:'relative', overflow:'hidden'
            }}>
                <div style={{
                    position:'absolute', top:'50%', left:'50%',
                    transform:'translate(-50%,-50%)',
                    width:'600px', height:'600px',
                    background:'radial-gradient(circle,rgba(201,169,110,.15) 0%,transparent 70%)',
                    borderRadius:'50%', pointerEvents:'none'
                }} />

                <div style={{ maxWidth:900, margin:'0 auto', textAlign:'center',
                              position:'relative', zIndex:1 }}>
                    <div style={{ display:'flex', justifyContent:'center', marginBottom:22 }}>
                        <StarIcon size={52} fill />
                    </div>
                    <h2 style={{ fontSize:'clamp(24px,4vw,42px)', fontWeight:300,
                                 color:'var(--text-light)', marginBottom:16,
                                 letterSpacing:'-.5px' }}>
                        Ready to Transform Your{' '}
                        <span style={{ color:'var(--accent-color)' }}>Space</span>?
                    </h2>
                    <p style={{ fontSize:isMobile?'16px':'18px',
                                color:'rgba(245,241,237,.7)',
                                lineHeight:1.7, maxWidth:700,
                                margin:'0 auto 38px' }}>
                        Explore our 8 specialized design studios and discover how we can bring
                        your vision to life with 25+ years of expertise.
                    </p>
                    <div className="bd-btn-row">
                        <Link to="/studios" className="bd-cta-primary">
                            Explore Studios <ArrowIcon size={18} />
                        </Link>
                        <Link to="/contact" className="bd-cta-outline">
                            Contact Us <ChatIcon size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ════════════════════ FOOTER ════════════════════ */}
            <footer>
                <img
                    src={`${process.env.PUBLIC_URL}/current-logo.png`}
                    alt="Hav'n Interiors"
                    style={{ width: 160, opacity: .8 }}
                />
                <p>&copy; 2025 Hav'n Interiors &amp; Global Sourcing. All Rights Reserved.</p>
            </footer>
        </div>
    );
};

export default BlogDetailPage;
